import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faCalendarAlt, faMoon, faStar } from "@fortawesome/free-solid-svg-icons";

const TripSummaryHeader = ({ tripData, itinerary }) => {
  if (!tripData) return null;

  const destinations = tripData.destinations || [];
  
  // Use itinerary.tripSummary.citiesBreakdown if available to correctly handle duplicate cities with different nights
  const cityPlans = itinerary?.tripSummary?.citiesBreakdown || [];
  
  const nightsArray = destinations.map((d, index) => {
    // If we have generated cityPlans, use the nights from the matching index
    if (cityPlans[index] && (cityPlans[index].city === d.name || cityPlans[index].city === d.city)) {
      return cityPlans[index].nights;
    }
    
    // Fallback to customNightsPerDestination map
    let n = 0;
    if (tripData.customNightsPerDestination && tripData.customNightsPerDestination[d.name] !== undefined) {
      n = tripData.customNightsPerDestination[d.name];
    } else if (tripData.customNightsPerDestination && tripData.customNightsPerDestination[d.city] !== undefined) {
      n = tripData.customNightsPerDestination[d.city];
    }
    return n;
  });

  const daysArray = [...nightsArray];
  if (daysArray.length > 0) {
    daysArray[0] += 1;
  }

  const destNames = destinations.map((d, index) => {
    const n = nightsArray[index];
    const days = daysArray[index] === 0 ? 1 : daysArray[index];
    return `${d.name} (${n}N/${days}D)`;
  }).join(", ") || "Unknown";

  const formatDateRange = (start, end) => {
    if (!start || !end) return "Dates TBD";
    const s = new Date(start);
    const e = new Date(end);
    const startDay = s.getDate();
    const endDay = e.getDate();
    const month = e.toLocaleString('default', { month: 'short' });
    return `${startDay}-${endDay} ${month}`;
  };

  const calculateNights = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    return Math.max(0, Math.ceil((e - s) / (1000 * 60 * 60 * 24)));
  };

  const nights = tripData.totalNights || calculateNights(tripData.startDate, tripData.endDate);

  return (
    <>
      {tripData.averageRating > 0 && (
        <div className="flex items-center gap-2 mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-lg max-w-fit shadow-sm">
          <FontAwesomeIcon icon={faStar} className="text-yellow-500" />
          <span className="font-bold">{tripData.averageRating.toFixed(1)}</span>
          <span className="text-sm font-medium opacity-80">({tripData.reviewCount} {tripData.reviewCount === 1 ? 'review' : 'reviews'})</span>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
      <div className="col-span-2 sm:col-span-1 border border-gray-200 rounded-lg p-3 text-center bg-white flex flex-col items-center justify-center shadow-sm overflow-hidden">
        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Destination</span>
        <div className="flex items-start sm:items-center justify-center gap-1.5 text-gray-800 font-semibold text-xs md:text-sm px-1 w-full">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="text-teal-600 mt-0.5 sm:mt-0 shrink-0" />
          <span className="break-words whitespace-normal line-clamp-2">{destNames}</span>
        </div>
      </div>
      <div className="border border-gray-200 rounded-lg p-3 text-center bg-white flex flex-col items-center justify-center shadow-sm">
        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Travel Date</span>
        <div className="flex items-center gap-1.5 text-gray-800 font-semibold text-xs md:text-sm">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-teal-600" />
          <span>{formatDateRange(tripData.startDate, tripData.endDate)}</span>
        </div>
      </div>
      <div className="border border-gray-200 rounded-lg p-3 text-center bg-white flex flex-col items-center justify-center shadow-sm">
        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Duration</span>
        <div className="flex items-center gap-1.5 text-gray-800 font-semibold text-xs md:text-sm">
          <FontAwesomeIcon icon={faMoon} className="text-teal-600" />
          <span>{nights}N/{nights + 1}D</span>
        </div>
      </div>
    </div>
    </>
  );
};

export default TripSummaryHeader;
